import { useEffect, useMemo, useState } from "react";
import { X, MapPin, ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import { PROJECTS, PROJECT_ORDER } from "../data/site";

const FALLBACK_IMG =
  "https://images.unsplash.com/photo-1565608438257-fac3c27beb36?w=1200&q=80";

function onImgError(e) {
  if (e.target.dataset.fallbackApplied) return;
  e.target.dataset.fallbackApplied = "1";
  e.target.src = FALLBACK_IMG;
}

function ProjectModal({ project, onClose }) {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    setIdx(0);
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight")
        setIdx((i) => (i + 1) % project.images.length);
      if (e.key === "ArrowLeft")
        setIdx((i) => (i - 1 + project.images.length) % project.images.length);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [project, onClose]);

  return (
    <div
      data-testid="project-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 backdrop-blur-md p-4 md:p-8 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-6xl bg-bg border border-white/10 my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="project-modal-close"
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 grid place-items-center bg-black/70 border border-white/10 hover:border-brand hover:text-brand text-white transition-colors"
          aria-label="Zatvori"
        >
          <X size={18} />
        </button>

        {/* Image carousel */}
        <div className="relative aspect-[16/10] bg-black overflow-hidden">
          <img
            src={project.images[idx]}
            alt={`${project.title} ${idx + 1}`}
            onError={onImgError}
            className="w-full h-full object-cover"
          />
          {project.images.length > 1 && (
            <>
              <button
                data-testid="modal-prev-image"
                onClick={() =>
                  setIdx((i) => (i - 1 + project.images.length) % project.images.length)
                }
                className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center bg-black/70 border border-white/10 hover:border-brand hover:text-brand text-white transition-colors"
                aria-label="Prethodna"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                data-testid="modal-next-image"
                onClick={() => setIdx((i) => (i + 1) % project.images.length)}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center bg-black/70 border border-white/10 hover:border-brand hover:text-brand text-white transition-colors"
                aria-label="Sljedeća"
              >
                <ChevronRight size={20} />
              </button>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
                {project.images.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setIdx(i)}
                    aria-label={`Slika ${i + 1}`}
                    className={`h-1.5 transition-all ${
                      i === idx ? "w-8 bg-brand" : "w-3 bg-white/40"
                    }`}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Content */}
        <div className="p-6 md:p-10 grid md:grid-cols-12 gap-8">
          <div className="md:col-span-7">
            <div className="text-xs uppercase tracking-widest2 text-brand flex items-center gap-2 mb-3">
              <MapPin size={12} />
              {project.location}
            </div>
            <h3 className="font-display font-black text-white tracking-tightest text-3xl md:text-4xl leading-tight mb-5">
              {project.title}
            </h3>
            <p className="text-ink-muted leading-relaxed">
              {project.description}
            </p>
          </div>
          <div className="md:col-span-5">
            <div className="text-[10px] uppercase tracking-widest2 text-ink-muted mb-4">
              Što smo izveli
            </div>
            <ul className="space-y-3">
              {project.bullets.map((b, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 text-sm text-white border-l border-brand pl-4 py-1"
                >
                  {b}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Projects() {
  const [active, setActive] = useState(null);
  const [showAll, setShowAll] = useState(false);

  // Apply PROJECT_ORDER to sort projects; any project not listed falls to the end.
  const orderedProjects = useMemo(() => {
    const byId = Object.fromEntries(PROJECTS.map((p) => [p.id, p]));
    const ordered = PROJECT_ORDER.map((id) => byId[id]).filter(Boolean);
    const leftovers = PROJECTS.filter((p) => !PROJECT_ORDER.includes(p.id));
    return [...ordered, ...leftovers];
  }, []);

  const visible = showAll ? orderedProjects : orderedProjects.slice(0, 9);

  // IDs that should start a fresh row on lg+ screens (3-col grid).
  const rowBreakIds = new Set();

  // Make newly-added cards immediately visible (skip reveal animation for them)
  useEffect(() => {
    if (!showAll) return;
    requestAnimationFrame(() => {
      document
        .querySelectorAll('#projekti [data-project-card]')
        .forEach((el) => el.classList.add("is-visible"));
    });
  }, [showAll]);

  return (
    <section
      id="projekti"
      data-testid="projects-section"
      className="relative py-24 md:py-32 bg-bg border-t border-white/5 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid md:grid-cols-12 gap-10 items-end mb-14 md:mb-20">
          <div className="md:col-span-7 reveal">
            <div className="text-xs uppercase tracking-widest2 text-brand flex items-center gap-3 mb-6">
              <span className="w-8 h-px bg-brand" />
              Projekti
            </div>
            <h2 className="font-display font-black text-white tracking-tightest leading-[0.95] text-4xl md:text-5xl lg:text-6xl">
              Naša ruka stoji iza
              <br />
              <span className="text-brand">stotina objekata.</span>
            </h2>
          </div>
          <p className="md:col-span-5 text-ink-muted leading-relaxed md:pb-3 reveal">
            Od trgovačkih centara i poliklinika do stambenih kvartova i
            modernih vila — pregled odabranih realizacija iz naše prakse.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {visible.map((p, i) => (
            <button
              key={p.id}
              data-testid={`project-card-${p.id}`}
              data-project-card
              onClick={() => setActive(p)}
              className={`group relative text-left bg-surface border border-white/10 hover:border-brand/60 overflow-hidden hover-lift ${
                i < 9 ? "reveal" : "is-visible"
              } ${rowBreakIds.has(p.id) ? "lg:col-start-1" : ""}`}
              style={i < 9 ? { animationDelay: `${(i % 3) * 0.08}s` } : undefined}
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={p.cover}
                  alt={p.title}
                  loading="lazy"
                  onError={onImgError}
                  className="absolute inset-0 w-full h-full object-cover zoom-img"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                <span className="absolute top-4 left-4 text-[10px] uppercase tracking-widest2 text-white bg-black/60 backdrop-blur-sm px-2.5 py-1.5 border border-white/10">
                  <MapPin size={10} className="inline mr-1.5 -mt-0.5" />
                  {p.location}
                </span>
              </div>
              <div className="p-5 md:p-6">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-display font-bold text-white text-lg md:text-xl tracking-tight leading-tight">
                    {p.title}
                  </h3>
                  <ArrowUpRight
                    size={18}
                    className="slide-arrow text-ink-muted group-hover:text-brand flex-shrink-0 mt-1"
                  />
                </div>
                <p className="mt-2 text-sm text-ink-muted leading-relaxed line-clamp-2">
                  {p.short}
                </p>
                <span className="mt-4 inline-block text-[10px] uppercase tracking-widest2 text-brand">
                  Saznaj više →
                </span>
              </div>
            </button>
          ))}
        </div>

        {!showAll && orderedProjects.length > 9 && (
          <div className="mt-12 flex justify-center">
            <button
              data-testid="show-all-projects"
              onClick={() => setShowAll(true)}
              className="inline-flex items-center gap-3 border border-white/20 hover:border-brand hover:text-brand text-white px-7 py-4 text-xs font-bold uppercase tracking-widest2 transition-colors"
            >
              Prikaži sve projekte ({orderedProjects.length})
              <ArrowUpRight size={16} className="slide-arrow" />
            </button>
          </div>
        )}
      </div>

      {active && <ProjectModal project={active} onClose={() => setActive(null)} />}
    </section>
  );
}
